import React, { useState, useMemo, useCallback } from "react";
import ChannelButton from "./ChannelButton";
import "./VehicleList.css";
import { Tag } from "antd";
import trans from "../../../utils";

const VehicleList = ({
  vehicles,
  groupedVehicles,
  groups,
  onChannelClick,
  isChannelActive,
  isLoading,
}) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = useCallback((groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  }, []);

  // Create a map for quick group lookup
  const groupsMap = useMemo(() => {
    const map = {};
    if (groups && Array.isArray(groups)) {
      groups.forEach((group) => {
        map[String(group.id)] = group;
      });
    }
    return map;
  }, [groups]);

  // Render vehicles grouped by group - memoized
  const renderGroupedVehicles = useMemo(() => {
    if (!groupedVehicles || Object.keys(groupedVehicles).length === 0) {
      return null;
    }

    const groupKeys = Object.keys(groupedVehicles);

    return groupKeys.map((groupKey) => {
      const groupVehicles = groupedVehicles[groupKey];
      // Use map for O(1) lookup instead of find
      const group = groupsMap[groupKey] || {
        name: groupKey === "ungrouped" ? "Ungrouped" : `Group ${groupKey}`,
      };
      const isExpanded = expandedGroups[groupKey] !== false; // Default to expanded

      return (
        <div key={groupKey} className="vehicle-group">
          <div className="group-header" onClick={() => toggleGroup(groupKey)}>
            <span className="group-arrow">{isExpanded ? "▼" : "▶"}</span>
            <span className="group-name">{group.name}</span>
          </div>
          {isExpanded && (
            <div className="group-vehicles">
              {groupVehicles.map((vehicle) => (
                <VehicleItem
                  key={vehicle.id}
                  vehicle={vehicle}
                  onChannelClick={onChannelClick}
                  isChannelActive={isChannelActive}
                />
              ))}
            </div>
          )}
        </div>
      );
    });
  }, [
    groupedVehicles,
    groupsMap,
    expandedGroups,
    toggleGroup,
    onChannelClick,
    isChannelActive,
  ]);

  // NOW we can do conditional returns AFTER all hooks
  if (isLoading) {
    return (
      <div className="vehicle-list-loading">
        <div className="loading-text">Loading vehicles...</div>
      </div>
    );
  }

  if (!vehicles || vehicles.length === 0) {
    return (
      <div className="vehicle-list-empty">
        <div className="empty-text">No vehicles found</div>
      </div>
    );
  }

  return <div className="vehicle-list">{renderGroupedVehicles}</div>;
};

const VehicleItem = React.memo(
  ({ vehicle, onChannelClick, isChannelActive }) => {
    const statusClass =
      vehicle.status === "online" ? "status-online" : "status-offline";
    const statusText =
      vehicle.status === "online" ? trans("Online") : trans("offline");

    const handleChannelClick = useCallback(
      (channel) => {
        onChannelClick(vehicle, channel);
      },
      [vehicle, onChannelClick],
    );

    return (
      <div className="vehicle-item">
        <div className="vehicle-header">
          <div className="vehicle-name">{vehicle.name}</div>
          <Tag color={statusClass === "status-online" ? "green" : "red"}>
            {statusText}
          </Tag>
        </div>
        <div className="vehicle-imei">IMEI: {vehicle.imei}</div>
        <div className="vehicle-channels">
          {vehicle.channels.map((channel) => (
            <ChannelButton
              key={channel.number}
              channel={channel}
              vehicle={vehicle}
              isActive={isChannelActive(vehicle.id, channel.number)}
              onClick={() => handleChannelClick(channel)}
            />
          ))}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Optimized comparison - only check essential props
    if (prevProps.vehicle.id !== nextProps.vehicle.id) return false;
    if (prevProps.vehicle.name !== nextProps.vehicle.name) return false;
    if (prevProps.vehicle.status !== nextProps.vehicle.status) return false;
    if (prevProps.vehicle.imei !== nextProps.vehicle.imei) return false;
    if (prevProps.vehicle.channels.length !== nextProps.vehicle.channels.length)
      return false;

    // Only check channel active state if isChannelActive function reference changed
    // This prevents expensive calls on every render
    if (prevProps.isChannelActive !== nextProps.isChannelActive) {
      // Function changed, need to re-render to get new active states
      return false;
    }

    return true;
  },
);

export default VehicleList;
